import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Skeleton, Tab, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, Tabs } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PageContainer from '../../components/PageContainer';
import { AppDialog, EmptyState, PageHeader, Panel, StatusChip } from '../../components/ui';
import { getShipmentIssues, ignoreShipmentIssue, voidShipmentInShipStation, recheckShipmentIssue, getManualShipments } from '../../api/shipments';
import MultipleLabelPopup from './MultipleLabelPopup';
import CreateShipmentPopup from './CreateShipmentPopup';
import { Notifications } from '../../store/slices';
import { formatAmount, formatDate } from '../../lib/formatDate';
import { Tone } from '../../theme';
import { useStyles } from './styles';

const ISSUE_STATUS_TONE: Record<string, Tone> = { OPEN: 'danger', RESOLVED: 'success', IGNORED: 'neutral' };

/** "VOID_MISMATCH" → "Void mismatch". */
const humanize = (code: unknown): string => {
    if (typeof code !== 'string' || code.length === 0) return '—';
    const words = code.toLowerCase().replace(/_/g, ' ');
    return words.charAt(0).toUpperCase() + words.slice(1);
};

const ShipmentIssuesPage = () => {
    const classes = useStyles();
    const [issues, setIssues] = useState<any[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [loading, setLoading] = useState(false);
    const [currentTab, setCurrentTab] = useState(0);

    const [manualShipments, setManualShipments] = useState<any[]>([]);
    const [manualTotalCount, setManualTotalCount] = useState(0);

    const dispatch = useDispatch();

    // Popup state
    const [popupOpen, setPopupOpen] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
    const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
    const [selectedIssueId, setSelectedIssueId] = useState<number | null>(null);

    // Create Shipment Popup state
    const [createShipmentPopupOpen, setCreateShipmentPopupOpen] = useState(false);

    // Confirm Dialog state
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [confirmDialogTitle, setConfirmDialogTitle] = useState('');
    const [confirmDialogContent, setConfirmDialogContent] = useState('');
    const [confirmDialogDanger, setConfirmDialogDanger] = useState(false);
    const [confirmDialogAction, setConfirmDialogAction] = useState<(() => Promise<void>) | null>(null);
    const [confirmLoading, setConfirmLoading] = useState(false);

    const fetchIssues = async () => {
        setLoading(true);
        try {
            const response = await getShipmentIssues({
                limit: rowsPerPage,
                page: page + 1,
            });
            setIssues(response.result || []);
            setTotalCount(response.totalCount || 0);
        } catch (error) {
            console.error('Error fetching shipment issues:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchManualShipments = async () => {
        setLoading(true);
        try {
            const response = await getManualShipments({
                limit: rowsPerPage,
                page: page + 1,
            });
            setManualShipments(response.result || []);
            setManualTotalCount(response.totalCount || 0);
        } catch (error) {
            console.error('Error fetching manual shipments:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (currentTab === 0) {
            fetchIssues();
        } else {
            fetchManualShipments();
        }
        // Reload on paging or tab changes only; the fetchers read the same state.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage, currentTab]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setCurrentTab(newValue);
        setPage(0);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const confirmAction = (title: string, content: string, action: () => Promise<void>, danger = false) => {
        setConfirmDialogTitle(title);
        setConfirmDialogContent(content);
        setConfirmDialogDanger(danger);
        setConfirmDialogAction(() => action);
        setConfirmDialogOpen(true);
    };

    const executeConfirmAction = async () => {
        if (confirmLoading) return;
        if (confirmDialogAction) {
            setConfirmLoading(true);
            await confirmDialogAction();
            setConfirmLoading(false);
        }
        setConfirmDialogOpen(false);
    };

    const handleIgnore = (issueId: number) => {
        confirmAction(
            'Ignore this issue?',
            `Issue ${issueId} is marked as ignored and leaves the open list. The shipment itself is not changed.`,
            async () => {
                try {
                    const response = await ignoreShipmentIssue({ issueId, onSuccess: () => {} });
                    dispatch(Notifications.setNotifications({ text: 'Issue ignored', type: 'success' }));
                    if (response && response.result) {
                        setIssues(prevIssues => prevIssues.map(issue => issue.issueId === response.result.issueId ? { ...issue, ...response.result } : issue));
                    } else {
                        fetchIssues(); // Fallback if no result is returned
                    }
                } catch (error: any) {
                    dispatch(Notifications.setNotifications({ text: error.message || 'Failed to ignore issue', type: 'error' }));
                    console.error(error);
                }
            }
        );
    };

    const handleVoidShipStation = (shipmentId: number, issueId: number) => {
        confirmAction(
            'Void the shipment in ShipStation?',
            `Shipment ${shipmentId} is voided on the ShipStation side and the issue is re-checked afterwards. This cannot be undone.`,
            async () => {
                try {
                    await voidShipmentInShipStation({ shipmentId, onSuccess: () => {} });
                    dispatch(Notifications.setNotifications({ text: 'Voided in ShipStation', type: 'success' }));
                    const recheckResponse = await recheckShipmentIssue({ issueId });
                    handlePopupSuccess(recheckResponse.result);
                } catch (error: any) {
                    dispatch(Notifications.setNotifications({ text: error.message || 'Failed to void in ShipStation', type: 'error' }));
                    console.error(error);
                }
            },
            true
        );
    };

    const handleOpenPopup = (orderId: number, clientId: number, issueId: number) => {
        setSelectedOrderId(orderId);
        setSelectedClientId(clientId);
        setSelectedIssueId(issueId);
        setPopupOpen(true);
    };

    const handlePopupClose = () => {
        setPopupOpen(false);
        setSelectedOrderId(null);
        setSelectedClientId(null);
        setSelectedIssueId(null);
    };

    const handlePopupSuccess = (recheckResult?: any) => {
        if (recheckResult) {
            setIssues(prevIssues => prevIssues.map(issue => issue.issueId === recheckResult.issueId ? { ...issue, ...recheckResult } : issue));
        } else {
            if (currentTab === 0) fetchIssues();
            else fetchManualShipments();
        }
    };

    const orderLink = (clientId: unknown, orderId: unknown) => (
        <a href={`/${clientId}/orders/${orderId}/details`} target="_blank" rel="noreferrer" className={classes.link} title="Open the order in a new tab">
            #{String(orderId ?? '—')}
        </a>
    );

    const skeleton = (
        <div className={classes.skeletons} role="status" aria-label="Loading">
            {[0, 1, 2, 3, 4].map((row) => <Skeleton key={row} animation="wave" height={44} />)}
        </div>
    );

    const issuesTable = (
        <div className={classes.scroll}>
            <Table aria-label="Shipment issues">
                <TableHead>
                    <TableRow>
                        <TableCell>Issue</TableCell>
                        <TableCell>Order</TableCell>
                        <TableCell>Client</TableCell>
                        <TableCell>Shipment</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Tracking number</TableCell>
                        <TableCell>First detected</TableCell>
                        <TableCell align="right">Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {issues.map((issue) => (
                        <TableRow key={issue.issueId} hover>
                            <TableCell className={classes.mono}>{issue.issueId}</TableCell>
                            <TableCell>{orderLink(issue.clientId, issue.orderId)}</TableCell>
                            <TableCell className={classes.nowrap}>{issue.clientName || issue.clientId || '—'}</TableCell>
                            <TableCell className={classes.mono}>{issue.shipmentId ?? '—'}</TableCell>
                            <TableCell className={classes.nowrap} title={issue.issueType}>{humanize(issue.issueType)}</TableCell>
                            <TableCell>
                                <StatusChip size="sm" dot tone={ISSUE_STATUS_TONE[issue.status] ?? 'neutral'}>{humanize(issue.status)}</StatusChip>
                            </TableCell>
                            <TableCell className={classes.mono}>{issue.trackingNumber || '—'}</TableCell>
                            <TableCell className={classes.nowrap}>{formatDate(issue.firstDetectedAt, { time: true })}</TableCell>
                            <TableCell>
                                <div className={classes.actions}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => handleIgnore(issue.issueId)}
                                        disabled={issue.status === 'IGNORED' || issue.status === 'RESOLVED' || issue.issueType === 'MULTIPLE_LABEL'}
                                    >
                                        Ignore
                                    </Button>
                                    {issue.issueType === 'VOID_MISMATCH' && issue.status === 'OPEN' && (
                                        <Button variant="contained" size="small" onClick={() => handleVoidShipStation(issue.shipmentId, issue.issueId)}>
                                            Void in ShipStation
                                        </Button>
                                    )}
                                    {issue.issueType === 'MULTIPLE_LABEL' && issue.status === 'OPEN' && (
                                        <Button variant="contained" size="small" onClick={() => handleOpenPopup(issue.orderId, issue.clientId, issue.issueId)}>
                                            Void regular
                                        </Button>
                                    )}
                                </div>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );

    const manualTable = (
        <div className={classes.scroll}>
            <Table aria-label="Manual shipments">
                <TableHead>
                    <TableRow>
                        <TableCell>Client</TableCell>
                        <TableCell>Order</TableCell>
                        <TableCell>Shipment</TableCell>
                        <TableCell>Tracking number</TableCell>
                        <TableCell align="right">Cost</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Label</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {manualShipments.map((shipment, index) => (
                        <TableRow key={shipment.shipmentId || index} hover>
                            <TableCell className={classes.nowrap}>{shipment.clientName || shipment.clientId || '—'}</TableCell>
                            <TableCell>{orderLink(shipment.clientId, shipment.orderId)}</TableCell>
                            <TableCell className={classes.mono}>{shipment.shipmentId ?? '—'}</TableCell>
                            <TableCell className={classes.mono}>{shipment.trackingNumber || '—'}</TableCell>
                            <TableCell className={classes.numeric}>{formatAmount(shipment.shipmentCost)}</TableCell>
                            <TableCell className={classes.nowrap}>{formatDate(shipment.createDate, { time: true })}</TableCell>
                            <TableCell className={classes.nowrap}>{humanize(shipment.type)}</TableCell>
                            <TableCell>
                                <StatusChip size="sm" dot tone={shipment.voided ? 'danger' : 'success'}>{shipment.voided ? 'Voided' : 'Active'}</StatusChip>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );

    const rows = currentTab === 0 ? issues : manualShipments;

    return (
        <PageContainer constrained>
            <PageHeader
                eyebrow="API Integration"
                title="Shipments"
                actions={currentTab === 1 ? (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateShipmentPopupOpen(true)}>
                        Create shipment
                    </Button>
                ) : undefined}
            >
                <Tabs value={currentTab} onChange={handleTabChange} aria-label="Shipment issues and manual shipments">
                    <Tab label="Shipment issues" id="shipments-tab-0" aria-controls="shipments-panel-0" />
                    <Tab label="Manual shipments" id="shipments-tab-1" aria-controls="shipments-panel-1" />
                </Tabs>
            </PageHeader>

            <Panel padding="none">
                <div role="tabpanel" id={`shipments-panel-${currentTab}`} aria-labelledby={`shipments-tab-${currentTab}`}>
                    {loading && skeleton}
                    {!loading && rows.length > 0 && (currentTab === 0 ? issuesTable : manualTable)}
                    {!loading && rows.length === 0 && (
                        currentTab === 0
                            ? <EmptyState title="No shipment issues" hint="Issues appear here when a label does not match its order or an order has more than one label." />
                            : <EmptyState title="No manual shipments" hint="Shipments registered by hand show up here." action={<Button variant="outlined" size="small" onClick={() => setCreateShipmentPopupOpen(true)}>Create shipment</Button>} />
                    )}
                </div>
                <TablePagination
                    component="div"
                    count={currentTab === 0 ? totalCount : manualTotalCount}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Panel>

            <MultipleLabelPopup
                isOpen={popupOpen}
                onClose={handlePopupClose}
                orderId={selectedOrderId}
                clientId={selectedClientId}
                issueId={selectedIssueId}
                onSuccess={handlePopupSuccess}
            />

            <CreateShipmentPopup
                isOpen={createShipmentPopupOpen}
                onClose={() => setCreateShipmentPopupOpen(false)}
                onSuccess={fetchManualShipments}
            />

            <AppDialog
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                title={confirmDialogTitle}
                description={confirmDialogContent}
                pending={confirmLoading}
                maxWidth="xs"
                actions={(
                    <>
                        <Button variant="outlined" onClick={() => setConfirmDialogOpen(false)} disabled={confirmLoading}>Cancel</Button>
                        <Button variant="contained" color={confirmDialogDanger ? 'error' : 'primary'} onClick={executeConfirmAction} disabled={confirmLoading}>
                            Confirm
                        </Button>
                    </>
                )}
            />
        </PageContainer>
    );
};

export default ShipmentIssuesPage;
